import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { fadeUp } from "../../lib/motion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", business: "", package: "", message: "" };

export default function EnquiryForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please share your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSent(true);
      setForm(initial);
      toast.success("Thank you — your enquiry has been received.");
    } catch (err) {
      toast.error("Something went wrong. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enquire" data-testid="enquiry-section" className="py-24 sm:py-32 bg-[color:var(--bg-alt)]">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <motion.div {...fadeUp} className="lg:col-span-5">
          <p className="overline mb-6">— Enquire</p>
          <h2 className="font-serif-display font-light text-4xl sm:text-5xl tracking-tight text-[color:var(--charcoal)] leading-[1.08]">
            Let's refine the
            <span className="italic text-[color:var(--sage)]"> foundation </span>
            of your business.
          </h2>
          <p className="mt-8 text-[color:var(--text-secondary)] leading-relaxed font-light">
            Share where your business is now and what you'd like established. A personal response will follow within two working days.
          </p>
          <div className="mt-10 hairline" />
          <div className="mt-10 space-y-4 text-sm text-[color:var(--text-secondary)]">
            <p><span className="overline block mb-1">Email</span> info@thefoundationedit.co.uk</p>
            <p><span className="overline block mb-1">Response time</span> Within 2 working days</p>
          </div>
        </motion.div>

        <motion.form
          {...fadeUp}
          onSubmit={submit}
          data-testid="enquiry-form"
          className="lg:col-span-7 bg-[color:var(--surface)] border border-[color:var(--border-light)] p-8 sm:p-12 space-y-6"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="overline block mb-2">Name</label>
              <input data-testid="enquiry-name" className="input-editorial" value={form.name} onChange={update("name")} placeholder="Your name" />
            </div>
            <div>
              <label className="overline block mb-2">Email</label>
              <input data-testid="enquiry-email" type="email" className="input-editorial" value={form.email} onChange={update("email")} placeholder="you@business.com" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="overline block mb-2">Business</label>
              <input data-testid="enquiry-business" className="input-editorial" value={form.business} onChange={update("business")} placeholder="Business name (optional)" />
            </div>
            <div>
              <label className="overline block mb-2">Package interest</label>
              <select data-testid="enquiry-package-select" className="select-editorial" value={form.package} onChange={update("package")}>
                <option value="">Not sure yet</option>
                <option value="essential">The Essential Edit — £1,200</option>
                <option value="signature">The Signature Edit — £1,500</option>
                <option value="luxe">The Luxe Edit — £1,850</option>
              </select>
            </div>
          </div>
          <div>
            <label className="overline block mb-2">A little about where you are</label>
            <textarea
              data-testid="enquiry-message"
              rows={5}
              className="input-editorial resize-none"
              value={form.message}
              onChange={update("message")}
              placeholder="A note about your business and what you'd like established..."
            />
          </div>
          <div className="flex items-center justify-between pt-4">
            <p className="text-xs text-[color:var(--text-secondary)] tracking-wide">
              {sent ? "Application received — thank you." : "A personal response will follow."}
            </p>
            <button type="submit" disabled={loading} data-testid="enquiry-submit" className="btn-primary">
              {loading ? "Sending…" : "Apply to Work Together"}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
